/**
 * `iap-config` — resolving configuration for **native purchases** (Google
 * Play Billing). The switch that did not exist until now.
 *
 * ── Why it is needed ─────────────────────────────────────────────────────────
 * The audit `docs/auditoria/2026-08-13-verificacion-monetizacion-off.md`
 * identified the diagnosis: the `remove_ads` purchase was the **only** piece in this
 * repo without any feature flag. `initMonetization` only checked `isAndroid()`, the
 * `/parents` button did too, and the billing client connected to Play
 * on process startup. Additionally, it is the only app switch that turns on
 * **from outside the binary**: enabling the product in Play Console
 * affects already-installed APKs. For this reason, the gate cannot be cosmetic.
 *
 * ── The criterion: the decision goes into RESOLUTION, not at point of use ────
 * Same pattern as the tier clamp in `growth/config.ts`: when purchasing
 * is off, `removeAdsProductId` **is not `"remove_ads"`, it is `""`**. It is
 * not merely that the UI hides the button: the identifier that Play
 * requires does not exist in the resolved configuration. A caller that bypassed
 * the `enabled` check would still have nothing to purchase.
 *
 * ── Fail-closed ──────────────────────────────────────────────────────────────
 * Missing, blank, with spaces or an uninterpretable value
 * (`"maybe"`, `"1.0"`, `"si"`) ⇒ **disabled**. Only explicit affirmative
 * forms enable it. This is the opposite of `growth/config.ts`, where an
 * unintelligible value falls back to the default; here that nuance is unnecessary
 * because the default is ALREADY `false`, so "uninterpretable" and "disabled" coincide.
 *
 * ── What it intentionally DOES NOT have ──────────────────────────────────────
 * **There is no back-office override layer.** The ads kit does include it
 * (`applyAdsOverride`) and the audit itself points it out as latent surface:
 * the day someone wires it to remote data, turning ads on would reach
 * already-installed binaries. For purchases, that risk is not latent but
 * immediate —Play Console is already a remote switch—, so here the only
 * source is the **build env**, which requires recompiling and republishing.
 */

/** The catalog's only product: one-shot "remove ads" purchase. */
export const IAP_REMOVE_ADS_PRODUCT_ID = "remove_ads";

/** Resolved native-purchase configuration. */
export interface IapConfig {
  /** Can this app talk to Play Billing? By default, **no**. */
  enabled: boolean;
  /**
   * ID of the product to buy/restore. **Empty string when disabled**:
   * the value does not exist, rather than existing for someone to decide not to use.
   */
  removeAdsProductId: string;
}

/** Code defaults: purchase DISABLED. Enabling it is an explicit decision. */
export function builtinIapDefaults(): IapConfig {
  return { enabled: false, removeAdsProductId: "" };
}

/** The only forms that enable it. Anything else—including empty—disables it. */
const IAP_TRUTHY = new Set(["1", "true", "yes", "on"]);

function parseEnabled(raw: string): boolean {
  return IAP_TRUTHY.has(raw.trim().toLowerCase());
}

/**
 * Layer 2 — env. Both monorepo forms are accepted, as in the ads kit:
 * `VITE_IAP_ENABLED` (web bundle, what Vite bakes) and
 * `IAP_ENABLED` (server and e2e suites). Vite's takes precedence: if present,
 * it decides even when its value is unintelligible—which for this switch
 * means disable.
 *
 * It touches the configuration only if the variable EXISTS. Without the
 * variable there is no layer 2, and the code default takes precedence, which
 * makes "declare nothing" and "disabled" the same thing.
 *
 * ⚠️ The native side reads **the same two variables** at compile time
 * (`apps/desktop/src-tauri/src/lib.rs`, `iap_flag_says_yes`) to decide whether
 * to register the Play Billing plugin. If the names or truthy forms change
 * here, they must change there: this is one switch with two readers, and the
 * native reader is the one that actually stops the connection.
 */
export function applyIapEnv(config: IapConfig, env: Record<string, string | undefined>): void {
  const raw = env.VITE_IAP_ENABLED ?? env.IAP_ENABLED;
  if (raw !== undefined) {
    config.enabled = parseEnabled(raw);
  }
}

/**
 * Layer 3 — the clamp. **It is equivalent to the ads kit's tier clamp**:
 * the decision is made here, during resolution, rather than at every point
 * of use. Without `enabled`, the product ID does not even exist, so a caller
 * that skipped the check would have nothing to request from Play.
 *
 * It only tightens and is idempotent: applying it twice gives the same result.
 */
export function clampIapConfig(config: IapConfig): void {
  config.removeAdsProductId = config.enabled ? IAP_REMOVE_ADS_PRODUCT_ID : "";
}

/** Full resolution: defaults → env → clamp. */
export function resolveIapConfig(env: Record<string, string | undefined>): IapConfig {
  const config = builtinIapDefaults();
  applyIapEnv(config, env);
  clampIapConfig(config);
  return config;
}
